// Coastal rocks: sea stacks standing out of the shallows and boulders at cliff feet, each with a small foam ring
// at its base. Placement is pure (coastal.ts); this renders it as one instanced mesh per silhouette plus ONE
// instanced foam-ring mesh, all sized once per terrain signature (renderer-crash-rca: flat draw calls and
// memory, no per-publish allocation). Fog of war only rewrites the instance matrices in place.

import type { SceneNode } from '@aod/protocol';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { RenderLayer } from './RenderLayer.js';
import { waterTime } from './TerrainWater.js';
import { type RockKind, type RockPiece, buildRocks, rockVisibility } from './coastal.js';
import { foamRingGeometry, rockGeometries } from './shore/rockGeometry.js';
import { shoreBoardFor } from './shore/shoreBoard.js';

const KINDS: ReadonlyArray<RockKind> = ['spire', 'mesa', 'twin', 'boulder'];

const PALETTE: Record<RockKind, readonly string[]> = {
  spire: ['#8a857a', '#7a766e', '#948f84', '#6f6c64'],
  mesa: ['#8e8878', '#7d786c', '#99927f'],
  twin: ['#867f74', '#76726a', '#918b7f'],
  boulder: ['#7d7970', '#6e6b64', '#8a867b'],
};

const rockMat = new THREE.MeshBasicMaterial({ color: 'rgb(255,255,255)', vertexColors: true });

const _col = new THREE.Color();
function toneColor(kind: RockKind, tone: number): THREE.Color {
  const list = PALETTE[kind];
  return _col.set(list[Math.min(list.length - 1, Math.floor(tone * list.length))]!);
}

/** A foam ring that breathes around a rock's foot; rides the same wave as the water so it stays on the surface. */
function createFoamMaterial(): THREE.MeshBasicMaterial {
  const mat = new THREE.MeshBasicMaterial({
    color: 'rgb(255,255,255)',
    transparent: true,
    depthWrite: false,
    fog: true,
  });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = waterTime;
    shader.vertexShader = `uniform float uTime;
      attribute float aRad;
      varying float _vRad;
      varying vec2 _vLoc;
      ${shader.vertexShader}`.replace(
      '#include <begin_vertex>',
      `#include <begin_vertex>
      _vRad = aRad;
      _vLoc = position.xz;
      vec4 _fw = instanceMatrix * vec4(transformed, 1.0);
      float _w = sin(_fw.x * 2.5 + uTime) * cos(_fw.z * 2.5 + uTime * 0.7);
      _w += 0.6 * sin(_fw.x * 5.0 + uTime * 1.3) * sin(_fw.z * 5.0 - uTime * 0.9);
      _w += 0.35 * sin((_fw.x + _fw.z) * 1.1 + uTime * 0.5);
      transformed.y += _w * 0.03;`,
    );
    shader.fragmentShader = `uniform float uTime;
      varying float _vRad;
      varying vec2 _vLoc;
      ${shader.fragmentShader}`.replace(
      '#include <color_fragment>',
      `#include <color_fragment>
      {
        float _ang = atan(_vLoc.y, _vLoc.x);
        float _band = 1.0 - smoothstep(0.0, 1.0, abs(_vRad - 0.35) * 2.2);
        float _surge = 0.55 + 0.45 * sin(_ang * 5.0 + uTime * 1.2) * sin(_ang * 3.0 - uTime * 0.8 + 1.0);
        float _pulse = 0.75 + 0.25 * sin(uTime * 1.6 + _ang * 2.0);
        diffuseColor = vec4(0.95, 0.98, 0.96, _band * _surge * _pulse * 0.85);
      }`,
    );
  };
  mat.customProgramCacheKey = () => 'coast-foam-ring';
  return mat;
}

const foamMat = createFoamMaterial();

interface BatchProps {
  readonly pieces: ReadonlyArray<RockPiece>;
  /** One 0/1 flag per piece (fog of war); a stable object while the flags do not change. */
  readonly flags: Uint8Array;
}

function RockBatch({ kind, pieces, flags }: BatchProps & { readonly kind: RockKind }) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const geo = useMemo(() => rockGeometries()[kind], [kind]);
  useEffect(() => {
    const mesh = ref.current;
    if (!mesh) return;
    const obj = new THREE.Object3D();
    pieces.forEach((p, i) => {
      obj.position.set(p.x, p.y, p.z);
      obj.rotation.set(0, p.rotY, 0);
      // Geometry radius is 0.5: scale by the diameter. Hidden (fogged) rocks collapse to nothing in place.
      const k = flags[i] ? 1 : 0;
      obj.scale.set(p.sx * 2 * k, p.sy * k, p.sz * 2 * k);
      obj.updateMatrix();
      mesh.setMatrixAt(i, obj.matrix);
      mesh.setColorAt(i, toneColor(kind, p.tone));
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.computeBoundingSphere();
  }, [kind, pieces, flags]);
  return (
    <instancedMesh
      name={`coast-rock-${kind}`}
      args={[geo, rockMat, pieces.length]}
      ref={ref}
      frustumCulled={false}
      // Rocks are decoration: they never take pointer events away from the tiles under them.
      raycast={() => null}
    />
  );
}

function FoamRings({ pieces, flags }: BatchProps) {
  const ref = useRef<THREE.InstancedMesh>(null);
  const geo = useMemo(() => foamRingGeometry(), []);
  useEffect(() => () => geo.dispose(), [geo]);
  useEffect(() => {
    const mesh = ref.current;
    if (!mesh) return;
    const obj = new THREE.Object3D();
    pieces.forEach((p, i) => {
      obj.position.set(p.x, p.surface + 0.014, p.z);
      obj.rotation.set(0, p.rotY, 0);
      const k = flags[i] ? p.foam : 0;
      obj.scale.set(k, 1, k);
      obj.updateMatrix();
      mesh.setMatrixAt(i, obj.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
    mesh.computeBoundingSphere();
  }, [pieces, flags]);
  return (
    <instancedMesh
      name="coast-foam"
      args={[geo, foamMat, pieces.length]}
      ref={ref}
      frustumCulled={false}
      raycast={() => null}
      renderOrder={RenderLayer.Terrain + 2}
    />
  );
}

/** One silhouette's instances: its pieces and the matching slice of the fog flags. */
function KindBatch({
  kind,
  rocks,
  idx,
  flags,
}: {
  readonly kind: RockKind;
  readonly rocks: ReadonlyArray<RockPiece>;
  readonly idx: ReadonlyArray<number>;
  readonly flags: Uint8Array;
}) {
  const pieces = useMemo(() => idx.map((i) => rocks[i]!), [rocks, idx]);
  const sub = useMemo(() => Uint8Array.from(idx, (i) => flags[i]!), [idx, flags]);
  return <RockBatch kind={kind} pieces={pieces} flags={sub} />;
}

export function CoastalDressing({
  tiles,
  visibleCoords,
  exploredCoords,
}: {
  readonly tiles: ReadonlyArray<SceneNode>;
  readonly visibleCoords?: ReadonlySet<string> | null;
  readonly exploredCoords?: ReadonlySet<string>;
}) {
  // Shore data is cached by terrain signature (shoreBoardFor); the rocks follow it, not the per-publish tiles.
  const board = shoreBoardFor(tiles);
  const rocks = useMemo(() => buildRocks(board), [board]);
  // Rocks of each silhouette (with their index in `rocks`, so the fog flags line up).
  const byKind = useMemo(
    () =>
      KINDS.map((k) => {
        const idx: number[] = [];
        rocks.forEach((p, i) => p.kind === k && idx.push(i));
        return [k, idx] as const;
      }).filter(([, idx]) => idx.length > 0),
    [rocks],
  );
  // The fog flags change rarely, but visibleCoords is a fresh Set on most publishes: hand the batches the same
  // flags object until the flags really change, so their matrices are rewritten only then.
  const prevFlags = useRef<Uint8Array>(new Uint8Array(0));
  const next = rockVisibility(rocks, visibleCoords, exploredCoords);
  const same =
    prevFlags.current.length === next.length && prevFlags.current.every((v, i) => v === next[i]);
  if (!same) prevFlags.current = next;
  const flags = prevFlags.current;
  return (
    <>
      {byKind.map(([kind, idx]) => (
        <KindBatch
          key={`${kind}:${idx.length}`}
          kind={kind}
          rocks={rocks}
          idx={idx}
          flags={flags}
        />
      ))}
      {rocks.length > 0 && <FoamRings key={`foam:${rocks.length}`} pieces={rocks} flags={flags} />}
    </>
  );
}
